import cartItemActionTypes from './actionTypes';
import cartActionTypes from '../cart/actionTypes';
import createReducer from '../utils/createReducer';

const defaultState = {
  cartItemIds: [],
  cartItems: {},
  lineItemGroupData: {},
  hasDeferredPaymentItems: false,
};

/**
 * Expected CartItem state.
 * These fields should line up with any selectors that pull data from a cart item.
 */

const defaultCartItem = {
  id: undefined,
  groupId: undefined,
  ui: {
    blockUserInteraction: false,
    itemUpdateInProgress: false,
  },
  error: undefined,
  fulfillmentData: {
    fulfilledQuantity: undefined,
  },
  merchandisingData: {
    productName: undefined,
    merchandisableQuantitiesV2: undefined,
    productAttributes: [],
    customDisplayAttributes: [],
  },
  designData: {
    documentReferenceUrl: undefined,
    livePreviewUrl: undefined,
    editDocumentUrl: undefined,
    purcsData: {},
  },
  productData: {
    productKey: undefined,
    productVersion: undefined,
  },
  analyticsData: {},
  financeData: {},
};

export default createReducer(defaultState)({
  [cartActionTypes.CART_LOAD_SUCCESS]: (state, { cart }) => {
    const cartItems = {};
    const cartItemIds = [];
    let hasDeferredPaymentItems = false;
    if (cart && cart.lineItems) {
      cart.lineItems.forEach(lineItem => {
        cartItemIds.push(lineItem.id);
        cartItems[lineItem.id] = {
          ...defaultCartItem,
          id: lineItem.id,
          groupId: lineItem.groupId,
          fulfillmentData: {
            fulfilledQuantity: lineItem.fulfillmentData
              ? lineItem.fulfillmentData.fulfilledQuantity
              : undefined,
            fulfillmentSku: lineItem.fulfillmentData
              ? lineItem.fulfillmentData.fulfillmentSku
              : undefined,
            mcpProductVersion: lineItem.fulfillmentData
              ? lineItem.fulfillmentData.mcpProductVersion
              : undefined,
          },
          merchandisingData: {
            productName: lineItem.merchandisingData
              ? lineItem.merchandisingData.productName
              : undefined,
            merchandisableQuantitiesV2: lineItem.merchandisingData
              ? lineItem.merchandisingData.merchandisableQuantitiesV2
              : [],
            productAttributes: lineItem.merchandisingData
              ? lineItem.merchandisingData.productAttributes
              : [],
            customDisplayAttributes: lineItem.merchandisingData
              ? lineItem.merchandisingData.customDisplayAttributes
              : [],
            editOptionsUrl: lineItem.merchandisingData
              ? lineItem.merchandisingData.editOptionsUrl
              : undefined,
          },
          productData: {
            productKey: lineItem.productData
              ? lineItem.productData.productKey
              : undefined,
            selectedAttributes: lineItem.productData
              ? lineItem.productData.selectedAttributes
              : undefined,
            productVersion: lineItem.productData
              ? lineItem.productData.productVersion
              : undefined,
          },
          designData: {
            documentReferenceUrl: lineItem.designData
              ? lineItem.designData.documentReferenceUrl
              : undefined,
            livePreviewUrl: lineItem.designData
              ? lineItem.designData.livePreviewUrl
              : undefined,
            editDocumentUrl: lineItem.designData
              ? lineItem.designData.editDocumentUrl
              : undefined,
            purcsData: lineItem.designData ? lineItem.designData.purcsData : {},
          },
          analyticsData: lineItem.customOrderData
            ? lineItem.customOrderData.analytics
            : {},
          financeData: lineItem.customOrderData
            ? lineItem.customOrderData.finance
            : {},
          checkoutData: lineItem.customOrderData
            ? lineItem.customOrderData.checkout
            : {},
          legalRequirements: lineItem.customOrderData
            ? lineItem.customOrderData.legalRequirements
            : [],
        };

        if (lineItem.customOrderData?.payment?.collectDeferredPaymentAccount) {
          hasDeferredPaymentItems = true;
        }
      });
    }

    const lineItemGroupData = {};
    if (cart && cart.lineItemGroupData) {
      cart.lineItemGroupData.forEach(group => {
        lineItemGroupData[group.groupId] = group;
      });
    }
    return {
      cartItemIds,
      cartItems,
      lineItemGroupData,
      hasDeferredPaymentItems,
    };
  },
  [cartActionTypes.CART_CLEAR]: () => defaultState,
  [cartItemActionTypes.CARTITEMS_REMOVE_START]: (state, { itemId }) => ({
    ...state,
    cartItems: {
      ...state.cartItems,
      [itemId]: {
        ...state.cartItems[itemId],
        ui: {
          ...state.cartItems[itemId].ui,
          blockUserInteraction: true,
        },
      },
    },
  }),
  [cartItemActionTypes.CARTITEMS_REMOVE_FAIL]: (state, { itemId, error }) => ({
    ...state,
    cartItems: {
      ...state.cartItems,
      [itemId]: {
        ...state.cartItems[itemId],
        ui: {
          ...state.cartItems[itemId].ui,
          blockUserInteraction: false,
          error,
        },
      },
    },
  }),
  [cartItemActionTypes.CARTITEMS_REMOVE_SUCCESS]: (state, { itemId }) => ({
    ...state,
    cartItemIds: state.cartItemIds.filter(id => id !== itemId),
    cartItems: { ...state.cartItems, [itemId]: undefined },
  }),
  [cartItemActionTypes.CARTITEMGROUP_REMOVE_START]: (state, { itemIds }) => {
    const { cartItems } = state;

    itemIds.forEach(
      itemId =>
        (cartItems[itemId] = {
          ...state.cartItems[itemId],
          ui: {
            ...state.cartItems[itemId].ui,
            blockUserInteraction: true,
          },
        })
    );

    return {
      ...state,
      cartItems,
    };
  },
  [cartItemActionTypes.CARTITEMGROUP_REMOVE_FAIL]: (
    state,
    { itemIds, error }
  ) => {
    const { cartItems } = state;
    itemIds.forEach(
      itemId =>
        (cartItems[itemId].ui = {
          ...state.cartItems[itemId].ui,
          blockUserInteraction: false,
          error,
        })
    );

    return {
      ...state,
      cartItems,
    };
  },
  [cartItemActionTypes.CARTITEMGROUP_REMOVE_SUCCESS]: (
    state,
    { itemIds, groupId }
  ) => {
    const { cartItems, lineItemGroupData } = state;
    itemIds.forEach(itemId => (cartItems[itemId] = undefined));

    const cartItemIds = state.cartItemIds.filter(id => !itemIds.includes(id));

    Object.keys(lineItemGroupData).filter(id => id === groupId);

    return {
      ...state,
      cartItemIds,
      cartItems,
      lineItemGroupData,
    };
  },

  [cartItemActionTypes.CARTITEMS_QUANTITY_UPDATE_START]: (
    state,
    { itemId, newQuantity }
  ) => ({
    ...state,
    cartItems: {
      ...state.cartItems,
      [itemId]: {
        ...state.cartItems[itemId],
        ui: {
          ...state.cartItems[itemId].ui,
          blockUserInteraction: true,
          itemUpdateInProgress: true,
        },
        fulfillmentData: {
          ...state.cartItems[itemId].fulfillmentData,
          // optimistic quantity update to display user selected quantity immediately;
          // if update fails we roll back to original quantity
          fulfilledQuantity: newQuantity,
        },
      },
    },
  }),

  [cartItemActionTypes.CARTITEMS_QUANTITY_UPDATE_FAIL]: (
    state,
    { itemId, originalQuantity, error }
  ) => ({
    ...state,
    cartItems: {
      ...state.cartItems,
      [itemId]: {
        ...state.cartItems[itemId],
        ui: {
          ...state.cartItems[itemId].ui,
          blockUserInteraction: false,
          itemUpdateInProgress: false,
          error,
        },
        fulfillmentData: {
          ...state.cartItems[itemId].fulfillmentData,
          fulfilledQuantity: originalQuantity,
        },
      },
    },
  }),

  [cartItemActionTypes.CARTITEMS_QUANTITY_UPDATE_SUCCESS]: (
    state,
    { itemId }
  ) => ({
    ...state,
    cartItems: {
      ...state.cartItems,
      [itemId]: {
        ...state.cartItems[itemId],
        ui: {
          ...state.cartItems[itemId].ui,
        },
      },
    },
  }),
});
